<template>
    <v-card flat class="my-4">
      <v-card-title class="px-0">
        <v-btn color="primary" variant="flat" prepend-icon="mdi-plus" @click="openAddDialog">
          {{ $t("recruitment.apps.add") }}
        </v-btn>
      </v-card-title>
      <v-card-text>
        <CandidateApplicationList
          :rows="rows"
          @open-recruitment="goToRecruitmentDetails"
          @open-submission="openSubmission"
          @edit="openEditDialog"
          @delete="deleteApp"
        />
      </v-card-text>
    </v-card>
  
    <!-- Drawer add/edit (pattern operațional: popup din dreapta) -->
    <NcrmRecordEditDialog
      v-model="dialog"
      :title="isEdit ? $t('recruitment.apps.editTitle') : $t('recruitment.apps.addTitle')"
      header-icon="mdi-briefcase-plus-outline"
      :max-width="520"
      :save-disabled="!form.requirement"
      @save="isEdit ? updateApp() : addApp()"
    >
      <v-alert v-if="formError" type="error" density="compact" class="mb-4">
        {{ formError }}
      </v-alert>
      <v-autocomplete
        :label="$t('recruitment.apps.requirement')"
        v-model="form.requirement"
        :items="requirementItems"
        item-title="label"
        item-value="id"
        clearable
        hide-no-data
        :menu-props="selectMenuProps"
        :rules="[rules.required]"
        class="mb-4"
      />
      <v-select
        :label="$t('recruitment.apps.status')"
        v-model="form.status"
        :items="statusSelectItems"
        :menu-props="selectMenuProps"
        hide-details
      />
    </NcrmRecordEditDialog>

    <RecruitmentSubmissionDrawer
      v-model="submissionDrawerOpen"
      :application-id="selectedApplicationId"
      :read-only="!canSubmitCandidates"
      :initial-tab="submissionInitialTab"
      @sent="handleSubmissionSent"
    />
  </template>
  
  <script>
  import axios from "axios";
  import recruitmentSubmissionController from "@/Utile/recruitmentSubmissionController.mjs";
  import CandidateApplicationList from "@/components/candidate/CandidateApplicationList.vue";
  import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";

  export default {
    name: "Recrutare",
    mixins: [recruitmentSubmissionController],
    components: { CandidateApplicationList, NcrmRecordEditDialog },
    props: {
      candidateId: {
        type: [String, Number],
        required: true,
      },
    },
    data() {
      return {
        apps: [],
        requirements: [],
        dialog: false,
        isEdit: false,
        editId: null,
        formError: "",
        form: {
          requirement: null, // id din recruitment_requirements
          status: "Applied",
        },
        statusOptions: ["Sourcing", "LinkedIn", "Applied", "Screening", "Interview", "Offer", "Rejected", "Hired"],
        rules: {
          required: (v) =>
            !!(v || v === 0) || this.$t("crm.callDialog.required"),
        },
      };
    },
    computed: {
      requirementMap() {
        const m = new Map();
        (this.requirements || []).forEach((r) => m.set(r.id, r));
        return m;
      },
      rows() {
        return (this.apps || []).map((a) => {
          const r = this.requirementMap.get(a.id_recruitment_requirements) || {};
          return {
            id: a.id,
            id_recruitment_requirements: a.id_recruitment_requirements,
            status: a.status,
            added_at: this.formatDT(a.added_at),
            created_at: this.formatDT(a.created_at),
            name_companies: r.name_companies || "",
            name_vacancy: r.name_vacancy || "",
            requirement_type: r.requirement_type || "internal",
            client_company_name: r.client_company_name || "",
            submission_status: a.submission_status || "not_sent",
            submission_sent_count: Number(a.submission_sent_count || 0),
            submission_recipient_count: Number(a.submission_recipient_count || 0),
            submission_failed_count: Number(a.submission_failed_count || 0),
          };
        });
      },
      requirementItems() {
        return (this.requirements || []).map((r) => {
          const typeLabel = this.$t(`recruitment.requirementDrawer.${r.requirement_type === 'client' ? 'client' : 'internal'}`);
          return {
            id: r.id,
            label: `${r.name_companies} — ${r.name_vacancy} (${typeLabel})`,
          };
        });
      },
      // Etichete localizate pentru statusuri (business-first, nu enum brut englez).
      statusSelectItems() {
        return this.statusOptions.map((value) => ({ title: this.statusLabel(value), value }));
      },
      selectMenuProps() {
        return {
          maxHeight: 320,
          zIndex: 2710,
        };
      },
    },
    created() {
      this.fetchRequirements();
      this.fetchApps();
    },
    methods: {
      formatDT(dateString) {
        if (!dateString) return "";
        const s = String(dateString);
        if (s.includes("T")) {
          const [d, t] = s.split("T");
          return d + " " + (t || "").slice(0, 8);
        }
        return s.replace("Z", "");
      },
      statusLabel(status) {
        const key = `recruitment.status.${status}`;
        const translated = this.$t(key);
        return translated === key ? status || "—" : translated;
      },
      openAddDialog() {
        this.isEdit = false;
        this.editId = null;
        this.formError = "";
        this.form = {
          requirement: null,
          status: "Applied",
        };
        this.dialog = true;
      },
      openEditDialog(item) {
        this.isEdit = true;
        this.editId = item.id;
        this.formError = "";
        this.form = {
          requirement: item.id_recruitment_requirements,
          status: item.status || "Applied",
        };
        this.dialog = true;
      },
      closeDialog() {
        this.dialog = false;
      },
      fetchRequirements() {
        return axios
          .get("/candidati/recruitment_requirements")
          .then((res) => {
            this.requirements = Array.isArray(res.data) ? res.data : [];
          })
          .catch((err) => {
            console.error("Eroare la obținerea cerințelor:", err);
          });
      },
      fetchApps() {
        axios
          .get("/candidati/candidate_applications", {
            params: { id_candidates: this.candidateId },
          })
          .then((res) => {
            this.apps = Array.isArray(res.data) ? res.data : [];
          })
          .catch((err) => {
            console.error("Eroare la obținerea aplicațiilor:", err);
          });
      },
      addApp() {
        const reqId = this.form.requirement;
        if (!reqId) {
          this.formError = this.$t("recruitment.apps.selectRequirement");
          return;
        }
        this.formError = "";
        const payload = {
          id_candidates: this.candidateId,
          id_recruitment_requirements: reqId,
          status: this.form.status || "Applied",
        };
        axios
          .post("/candidati/candidate_applications", payload)
          .then(() => {
            this.dialog = false;
            this.fetchApps();
          })
          .catch((error) => {
            if (error?.response?.status === 409) {
              this.formError = this.$t("recruitment.apps.duplicate");
            } else {
              console.error("Eroare la adăugare aplicație:", error);
              this.formError = this.$t("recruitment.apps.addError");
            }
          });
      },
      updateApp() {
        if (!this.editId) {
          this.formError = this.$t("recruitment.apps.missingId");
          return;
        }
        this.formError = "";
        const reqId = this.form.requirement;
        const payload = {
          id_candidates: this.candidateId,
          id_recruitment_requirements: reqId,
          status: this.form.status || "Applied",
        };
        axios
          .put(`/candidati/candidate_applications/${this.editId}`, payload)
          .then(() => {
            this.dialog = false;
            this.fetchApps();
          })
          .catch((error) => {
            if (error?.response?.status === 409) {
              this.formError = this.$t("recruitment.apps.duplicate");
            } else {
              console.error("Eroare la actualizare aplicație:", error);
              this.formError = this.$t("recruitment.apps.updateError");
            }
          });
      },
      deleteApp(id) {
        if (!confirm(this.$t("recruitment.apps.deleteConfirm"))) return;
        axios
          .delete(`/candidati/candidate_applications/${id}`)
          .then(() => this.fetchApps())
          .catch((err) => {
            console.error("Eroare la ștergare aplicație:", err);
            alert(this.$t("recruitment.apps.deleteError"));
          });
      },
      goToRecruitmentDetails(id) {
        if (!id) return;
        this.$router.push({ name: "RecrutareDetails", params: { id } });
      },
	      onRecruitmentSubmissionSent(payload) {
	        this.fetchApps();
	        this.$emit("cv-sent", payload || {});
      },
    },
  };
  </script>
